/**
 * Pure Fortnite-like BR match rules — no DOM / WebGL / physics world.
 * Used by FPS systems at runtime and by unit tests so assertions drive shipped logic.
 */

/** Core fort piece kinds (mirrors FpsBuildPlacer without importing THREE). */
export type BuildPieceKind =
  | 'wall'
  | 'floor'
  | 'ramp'
  | 'cone'
  | 'pyramid'
  | 'trap'
  | 'launchpad';

/** Material cost per build piece — single source of truth for spend rules. */
export const BUILD_MAT_COST: Record<BuildPieceKind, number> = {
  wall: 10,
  floor: 10,
  ramp: 10,
  cone: 10,
  pyramid: 10,
  trap: 15,
  launchpad: 20,
};

// ─── Storm ───────────────────────────────────────────────────────────────────

export type StormSimState = {
  centerX: number;
  centerZ: number;
  radius: number;
  minRadius: number;
  shrinkPerSecond: number;
  damagePerSecond: number;
  damageMultiplier: number;
  outsideEdgeFactor: number;
  finalCircleSafeRadius: number;
};

export function isOutsideStorm(
  px: number,
  pz: number,
  centerX: number,
  centerZ: number,
  currentRadius: number,
  outsideEdgeFactor = 0.95,
): boolean {
  const dx = px - centerX;
  const dz = pz - centerZ;
  const distSq = dx * dx + dz * dz;
  const thresh = currentRadius * outsideEdgeFactor;
  return distSq > thresh * thresh;
}

/** Final-circle crunch: 2× damage when safe zone is smaller than threshold. */
export function finalCircleCrunchMultiplier(
  currentRadius: number,
  outsideEdgeFactor: number,
  finalCircleSafeRadius: number,
): number {
  const safeR = currentRadius * outsideEdgeFactor;
  return safeR < finalCircleSafeRadius ? 2 : 1;
}

export function computeStormTickDamage(
  dt: number,
  damagePerSecond: number,
  damageMultiplier = 1,
): number {
  if (dt <= 0 || damagePerSecond <= 0) return 0;
  return damagePerSecond * damageMultiplier * dt;
}

/** Damage this tick if player is outside the safe zone; 0 if inside. */
export function stormDamageAtPosition(
  px: number,
  pz: number,
  storm: Pick<
    StormSimState,
    | 'centerX'
    | 'centerZ'
    | 'radius'
    | 'damagePerSecond'
    | 'damageMultiplier'
    | 'outsideEdgeFactor'
    | 'finalCircleSafeRadius'
  >,
  dt: number,
): number {
  if (
    !isOutsideStorm(
      px,
      pz,
      storm.centerX,
      storm.centerZ,
      storm.radius,
      storm.outsideEdgeFactor,
    )
  ) {
    return 0;
  }
  const crunch = finalCircleCrunchMultiplier(
    storm.radius,
    storm.outsideEdgeFactor,
    storm.finalCircleSafeRadius,
  );
  return computeStormTickDamage(
    dt,
    storm.damagePerSecond,
    storm.damageMultiplier * crunch,
  );
}

/** Shrink radius toward min; returns new radius. */
export function shrinkStormRadius(
  currentRadius: number,
  shrinkPerSecond: number,
  minRadius: number,
  dt: number,
): number {
  if (dt <= 0 || shrinkPerSecond <= 0) return currentRadius;
  return Math.max(minRadius, currentRadius - shrinkPerSecond * dt);
}

// ─── Combat / elimination / win ──────────────────────────────────────────────

export type CombatVitals = {
  health: number;
  shield: number;
  alive: boolean;
};

export type DamageResult = {
  health: number;
  shield: number;
  alive: boolean;
  eliminated: boolean;
  appliedToShield: number;
  appliedToHealth: number;
};

/** Shield first, then health. Eliminated when health hits 0. */
export function applyCombatDamage(
  target: CombatVitals,
  damage: number,
): DamageResult {
  if (!target.alive || damage <= 0) {
    return {
      health: target.health,
      shield: target.shield,
      alive: target.alive,
      eliminated: false,
      appliedToShield: 0,
      appliedToHealth: 0,
    };
  }

  let remaining = damage;
  let shield = target.shield;
  let health = target.health;
  let appliedToShield = 0;
  let appliedToHealth = 0;

  if (shield > 0) {
    appliedToShield = Math.min(shield, remaining);
    shield -= appliedToShield;
    remaining -= appliedToShield;
  }
  if (remaining > 0) {
    appliedToHealth = Math.min(health, remaining);
    health -= appliedToHealth;
  }

  const eliminated = health <= 0;
  if (eliminated) health = 0;

  return {
    health,
    shield,
    alive: !eliminated,
    eliminated,
    appliedToShield,
    appliedToHealth,
  };
}

/** After an elimination, remaining opponents (bots/players). */
export function opponentsAfterElim(aliveOpponents: number): number {
  return Math.max(0, aliveOpponents - 1);
}

/**
 * Victory Royale when the local player is still alive and no opponents remain.
 * `initialOpponentCount > 0` avoids winning an empty match before spawn.
 */
export function isVictoryRoyale(
  playerAlive: boolean,
  opponentsAlive: number,
  initialOpponentCount: number,
): boolean {
  return playerAlive && initialOpponentCount > 0 && opponentsAlive === 0;
}

/** Placement rank: 1 = Victory Royale, higher = worse. */
export function placementFromAliveOpponents(opponentsAlive: number): number {
  return 1 + Math.max(0, opponentsAlive);
}

// ─── Harvest ─────────────────────────────────────────────────────────────────

export type HarvestPropKind = 'barrel' | 'fence' | 'tree' | 'rock' | 'crate';

/** Default kind for dynamic props that omit `userData.harvestProp` (loot crates). */
export const DEFAULT_HARVEST_KIND: HarvestPropKind = 'crate';

/** Mats granted when a prop breaks after enough pick hits (default break at 3). */
export function harvestBreakYield(
  kind: HarvestPropKind,
  rng: () => number = Math.random,
): number {
  switch (kind) {
    case 'barrel':
      return 8 + Math.floor(rng() * 5); // 8–12
    case 'fence':
      return 6 + Math.floor(rng() * 4); // 6–9
    case 'tree':
      return 12 + Math.floor(rng() * 9); // 12–20
    case 'rock':
      return 10 + Math.floor(rng() * 7); // 10–16
    case 'crate':
      return 7 + Math.floor(rng() * 6); // 7–12
    default:
      return 5;
  }
}

/**
 * After a swing, whether the prop breaks and how many mats to grant.
 * Untagged dynamic props use `DEFAULT_HARVEST_KIND` so crates still break after N hits.
 */
export function resolveHarvestSwing(
  previousHits: number,
  kind: HarvestPropKind | null,
  hitsToBreak = 3,
  rng: () => number = Math.random,
): { hits: number; broken: boolean; mats: number; kind: HarvestPropKind } {
  const effectiveKind = kind ?? DEFAULT_HARVEST_KIND;
  const hits = previousHits + 1;
  if (hits < hitsToBreak) {
    return { hits, broken: false, mats: 0, kind: effectiveKind };
  }
  return {
    hits,
    broken: true,
    mats: harvestBreakYield(effectiveKind, rng),
    kind: effectiveKind,
  };
}

export function addMaterials(current: number, gained: number, max = 999): number {
  return Math.min(max, Math.max(0, current + Math.max(0, gained)));
}

// ─── Building ────────────────────────────────────────────────────────────────

export type PlacedBuildRecord = {
  kind: BuildPieceKind;
  materialCost: number;
  gridX: number;
  gridY: number;
  gridZ: number;
};

export type BuildAttemptResult =
  | {
      ok: true;
      materialsLeft: number;
      cost: number;
      piece: PlacedBuildRecord;
    }
  | {
      ok: false;
      reason: 'no_materials' | 'invalid_kind' | 'occupied';
      materialsLeft: number;
      cost: number;
    };

export function buildPieceCost(kind: BuildPieceKind): number {
  return BUILD_MAT_COST[kind] ?? 10;
}

/**
 * Spend materials and record a placed fort piece (wall / floor / ramp / …).
 * Pure — does not touch THREE/CANNON; game layer mirrors this into the world.
 */
export function tryPlaceBuildPiece(
  materialsAvailable: number,
  kind: BuildPieceKind,
  gridX: number,
  gridY: number,
  gridZ: number,
  occupiedKeys: ReadonlySet<string> = new Set(),
): BuildAttemptResult {
  const cost = buildPieceCost(kind);
  if (!(kind in BUILD_MAT_COST)) {
    return { ok: false, reason: 'invalid_kind', materialsLeft: materialsAvailable, cost };
  }
  if (materialsAvailable < cost) {
    return { ok: false, reason: 'no_materials', materialsLeft: materialsAvailable, cost };
  }
  const key = `${kind}:${gridX},${gridY},${gridZ}`;
  if (occupiedKeys.has(key)) {
    return { ok: false, reason: 'occupied', materialsLeft: materialsAvailable, cost };
  }
  return {
    ok: true,
    materialsLeft: materialsAvailable - cost,
    cost,
    piece: { kind, materialCost: cost, gridX, gridY, gridZ },
  };
}

// ─── Movement / fall damage ──────────────────────────────────────────────────

const FALL_DAMAGE_SAFE_VY = -6;
const FALL_DAMAGE_PER_VY = 3.5;
const FALL_DAMAGE_MAX = 95;

/**
 * Fall damage from landing impact velocity (negative Y).
 * 0 while glider grace is active or impact is soft.
 */
export function computeFallDamage(
  impactVy: number,
  gliderGraceActive = false,
  safeVy = FALL_DAMAGE_SAFE_VY,
  perVy = FALL_DAMAGE_PER_VY,
  maxDmg = FALL_DAMAGE_MAX,
): number {
  if (gliderGraceActive || impactVy >= safeVy) return 0;
  return Math.min(maxDmg, Math.round((-impactVy - 6) * perVy));
}

/** Simple ground clamp used by kinematic helpers / tests. */
export function resolveGroundHeight(
  bodyY: number,
  terrainY: number,
  halfHeight: number,
): { y: number; grounded: boolean } {
  const floor = terrainY + halfHeight;
  if (bodyY <= floor + 0.02) {
    return { y: floor, grounded: true };
  }
  return { y: bodyY, grounded: false };
}

/**
 * Phase 30X/31X: proximity to storm edge (0 = far inside/out, 1 = on the rim).
 * Used for edge telegraph intensity and audio mix hooks.
 */
export function stormEdgeProximity(
  px: number,
  pz: number,
  centerX: number,
  centerZ: number,
  safeRadius: number,
  bandMeters = 12,
): number {
  if (safeRadius <= 0 || bandMeters <= 0) return 0;
  const dist = Math.hypot(px - centerX, pz - centerZ);
  const delta = Math.abs(dist - safeRadius);
  return Math.max(0, 1 - delta / bandMeters);
}

/** Hitstop duration (seconds) from damage — pure so tests cover combat juice scaling. */
export function hitStopFromDamage(
  damage: number,
  opts?: { headshot?: boolean; killed?: boolean },
): number {
  if (opts?.killed) return 0.05 + Math.min(0.04, damage * 0.00035);
  if (opts?.headshot) return 0.04 + Math.min(0.025, damage * 0.0003);
  if (damage >= 40) return 0.02 + Math.min(0.03, (damage - 40) * 0.0004);
  if (damage >= 18) return 0.01 + damage * 0.00015;
  return 0;
}

// ─── 32X combat depth pure rules ─────────────────────────────────────────────

/** Crit / headshot damage multiplier (body = 1). */
export function critDamageMultiplier(opts: {
  headshot?: boolean;
  crit?: boolean;
  headshotMult?: number;
  critMult?: number;
}): number {
  if (opts.headshot) return opts.headshotMult ?? 2;
  if (opts.crit) return opts.critMult ?? 1.5;
  return 1;
}

/** Apply headshot/crit mult then floor to integer damage. */
export function scaleDamageWithCrit(
  baseDamage: number,
  opts: { headshot?: boolean; crit?: boolean },
): number {
  if (baseDamage <= 0) return 0;
  return Math.max(1, Math.round(baseDamage * critDamageMultiplier(opts)));
}

/**
 * First-shot bloom recovery: after idle seconds, spread multiplies toward first-shot accuracy.
 * Returns spread multiplier (lower = tighter).
 */
export function bloomRecoverySpreadMult(
  timeSinceLastShot: number,
  idleThresholdS = 0.45,
  firstShotMult = 0.55,
): number {
  if (timeSinceLastShot >= idleThresholdS) return firstShotMult;
  // Linear recover from 1.0 (recent fire) toward firstShotMult
  const t = Math.max(0, Math.min(1, timeSinceLastShot / idleThresholdS));
  return 1 - t * (1 - firstShotMult);
}

/** Shotgun pellet cone: unit offsets in (u,v) for fixed patterns. */
export function shotgunPelletOffsets(
  pelletCount: number,
  pattern: 'pump' | 'tac' = 'pump',
): { u: number; v: number }[] {
  const n = Math.max(1, Math.min(12, Math.floor(pelletCount)));
  const out: { u: number; v: number }[] = [];
  // Center pellet always
  out.push({ u: 0, v: 0 });
  if (n === 1) return out;
  const ring = pattern === 'tac' ? 0.72 : 1;
  for (let i = 1; i < n; i++) {
    const a = ((i - 1) / (n - 1)) * Math.PI * 2;
    const r = ring * (0.55 + (i % 2) * 0.35);
    out.push({ u: Math.cos(a) * r, v: Math.sin(a) * r });
  }
  return out;
}

/** Tactical reload cancel window — fraction of reload remaining when cancel is allowed. */
export function reloadCancelWindow(reloadProgress01: number, cancelAfter = 0.22): boolean {
  return reloadProgress01 >= cancelAfter && reloadProgress01 < 1;
}

/** ADS camera sway damp multiplier (1 = full sway, lower = tighter aim). */
export function adsSwayDamp(adsActive: boolean, baseSway = 1, adsMult = 0.42): number {
  return adsActive ? baseSway * adsMult : baseSway;
}

/** Shield break intensity 0–1 from pre/post shield values. */
export function shieldBreakIntensity(shieldBefore: number, shieldAfter: number): number {
  if (shieldBefore <= 0) return 0;
  if (shieldAfter > 0) return 0;
  return Math.min(1, 0.55 + Math.min(shieldBefore, 100) / 200);
}

/**
 * Late-circle pressure: damage mult when alive players drop below threshold in late storm.
 * Returns 1 when no pressure.
 */
export function lateCirclePressureMult(
  stormPhase: number,
  alivePlayers: number,
  latePhase = 5,
  aliveThreshold = 10,
): number {
  if (stormPhase < latePhase || alivePlayers <= 0 || alivePlayers >= aliveThreshold) return 1;
  // Up to +25% when few remain in late circle
  const scarcity = 1 - alivePlayers / aliveThreshold;
  return 1 + scarcity * 0.25;
}

/** Bot flee urgency 0–1 when outside storm (higher = path harder toward safe center). */
export function botStormFleeUrgency(
  outside: boolean,
  distOutsideMeters: number,
  fullUrgencyAt = 40,
): number {
  if (!outside) return 0;
  return Math.max(0, Math.min(1, distOutsideMeters / Math.max(1, fullUrgencyAt)));
}

/** Active bot soft-cap for late circles (null = uncapped). */
export function lateCircleBotCap(stormPhase: number, isFinalCircle: boolean): number | null {
  if (stormPhase >= 5 || isFinalCircle) return 3;
  if (stormPhase >= 4) return 6;
  return null;
}
